// src/Pages/Dashboard/Sales/AddCreditNotes.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./AddCreditNotes.css";
import { API_URL } from "../../../config";

const AddCreditNotes = () => {
  const navigate = useNavigate();

  const [creditNoteNo, setCreditNoteNo] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [showCustomerWarning, setShowCustomerWarning] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);

  const [formData, setFormData] = useState({
    referenceNumber: "",
    creditNoteDate: new Date().toISOString().split("T")[0],
    paymentTerms: "Due on Receipt",
    subject: "",
    salesperson: "",
    items: [
      {
        id: Date.now(),
        itemDetails: "",
        account: "Sales",
        quantity: 1,
        rate: 0,
        amount: 0,
      },
    ],
    discountType: "%",
    discountValue: 0,
    tdsTcs: false,
    tdsTcsPercent: 0,
    adjustment: 0,
    customerNotes: "Thank you for your business.",
    termsConditions: "Standard credit note terms apply.",
  });

  // FETCH NEXT CREDIT NOTE NUMBER
  useEffect(() => {
    const fetchNextCN = async () => {
      try {
        const res = await fetch(`${API_URL}/api/credit-notes/latest`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setCreditNoteNo(data.nextNumber || "CN-00001");
        }
      } catch (err) {
        setCreditNoteNo("CN-00001");
      }
    };
    fetchNextCN();
  }, []);

  // FETCH CUSTOMERS
  useEffect(() => {
    const fetchCustomers = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      try {
        setLoadingCustomers(true);
        const res = await fetch(
          `${API_URL}/api/customers/get?userId=${userId}`,
          {
            credentials: "include",
          }
        );
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          const sorted = data.data.sort((a, b) =>
            getCustomerDisplayName(a).localeCompare(getCustomerDisplayName(b))
          );
          setCustomers(sorted);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, []);

  const getCustomerDisplayName = (cust) => {
    const fullName = [cust.firstName, cust.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const primary =
      fullName || cust.displayName || cust.companyName || "Unnamed Customer";
    const secondary =
      cust.companyName && cust.companyName !== primary
        ? ` (${cust.companyName})`
        : "";
    return primary + secondary;
  };

  // Item handlers
  const updateItem = (id, field, value) => {
    setFormData((prev) => {
      const updatedItems = prev.items.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === "quantity" || field === "rate") {
            updated.amount =
              Number(updated.quantity || 0) * Number(updated.rate || 0);
          }
          return updated;
        }
        return item;
      });
      return { ...prev, items: updatedItems };
    });
  };

  const removeItem = (id) => {
    if (formData.items.length === 1) return;
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }));
  };

  const addItemRow = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: Date.now(),
          itemDetails: "",
          account: "Sales",
          quantity: 1,
          rate: 0,
          amount: 0,
        },
      ],
    }));
  };

  // Calculations
  const subtotal = formData.items.reduce(
    (sum, item) => sum + (item.amount || 0),
    0
  );
  const discountAmount =
    formData.discountType === "%"
      ? (subtotal * formData.discountValue) / 100
      : formData.discountValue;
  const tdsTcsAmount = formData.tdsTcs
    ? (subtotal * formData.tdsTcsPercent) / 100
    : 0;
  const total = subtotal - discountAmount - tdsTcsAmount + formData.adjustment;

  // Save Credit Note
  const handleSave = async (status) => {
    if (!selectedCustomer) {
      setShowCustomerWarning(true);
      return;
    }

    const selectedCustObj = customers.find(
      (c) => getCustomerDisplayName(c) === selectedCustomer
    );

    const payload = {
      creditNoteNumber: creditNoteNo,
      customer: selectedCustObj?._id,
      customerName: selectedCustomer,
      referenceNumber: formData.referenceNumber,
      creditNoteDate: formData.creditNoteDate,
      paymentTerms: formData.paymentTerms,
      subject: formData.subject,
      salesperson: formData.salesperson,
      items: formData.items.map((i) => ({
        itemDetails: i.itemDetails,
        account: i.account,
        quantity: i.quantity,
        rate: i.rate,
        amount: i.amount,
      })),
      subTotal: subtotal,
      discount: discountAmount,
      tax: tdsTcsAmount,
      adjustment: formData.adjustment,
      total,
      totalQuantity: formData.items.reduce((s, i) => s + i.quantity, 0),
      customerNotes: formData.customerNotes,
      termsConditions: formData.termsConditions,
      status,
    };

    try {
      const res = await fetch(`${API_URL}/api/credit-notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (res.ok || result._id) {
        alert("Credit Note saved successfully!");
        navigate("/sales/credit-notes");
      } else {
        alert("Failed: " + (result.message || "Try again"));
      }
    } catch (err) {
      alert("Network error");
      console.error(err);
    }
  };

  return (
    <div className="add-credit-note-page">
      <div className="invoice-header">
        <h1>New Credit Note</h1>
      </div>

      <div className="credit-note-form">
        {/* Top Row: Customer + CN# */}
        <div className="form-row top-row">
          <div className="form-group customer-group">
            <label>
              Customer Name <span className="required">*</span>
            </label>
            <select
              value={selectedCustomer}
              onChange={(e) => {
                setSelectedCustomer(e.target.value);
                setShowCustomerWarning(false);
              }}
              disabled={loadingCustomers}
              className="customer-select"
            >
              <option value="">
                {loadingCustomers ? "Loading..." : "Select a customer"}
              </option>
              {customers.map((cust) => (
                <option key={cust._id} value={getCustomerDisplayName(cust)}>
                  {getCustomerDisplayName(cust)}
                </option>
              ))}
            </select>
            <div className="add-customer-link">
              <Link to="/sales/customers/new" className="link">
                + Add New Customer
              </Link>
            </div>
            {showCustomerWarning && (
              <small className="warning">Please select a customer</small>
            )}
          </div>

          <div className="form-group">
            <label>Credit Note#</label>
            <input type="text" value={creditNoteNo} readOnly />
          </div>
        </div>

        {/* Reference, Date, Terms */}
        <div className="form-row">
          <div className="form-group">
            <label>Reference#</label>
            <input
              type="text"
              value={formData.referenceNumber}
              onChange={(e) =>
                setFormData({ ...formData, referenceNumber: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Credit Note Date</label>
            <input
              type="date"
              value={formData.creditNoteDate}
              onChange={(e) =>
                setFormData({ ...formData, creditNoteDate: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Payment Terms</label>
            <select
              value={formData.paymentTerms}
              onChange={(e) =>
                setFormData({ ...formData, paymentTerms: e.target.value })
              }
            >
              <option>Due on Receipt</option>
              <option>Net 15</option>
              <option>Net 30</option>
              <option>Net 45</option>
              <option>Net 60</option>
            </select>
          </div>
        </div>

        {/* Salesperson + Subject */}
        <div className="form-row">
          <div className="form-group">
            <label>Salesperson</label>
            <input
              type="text"
              value={formData.salesperson}
              onChange={(e) =>
                setFormData({ ...formData, salesperson: e.target.value })
              }
            />
          </div>
          <div className="form-group full-width">
            <label>Subject</label>
            <input
              type="text"
              placeholder="Reason for credit note"
              value={formData.subject}
              onChange={(e) =>
                setFormData({ ...formData, subject: e.target.value })
              }
            />
          </div>
        </div>

        {/* Items Table */}
        <div className="item-section">
          <table className="item-table">
            <thead>
              <tr>
                <th>ITEM DETAILS</th>
                <th>ACCOUNT</th>
                <th>QUANTITY</th>
                <th>RATE</th>
                <th>AMOUNT</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {formData.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <input
                      type="text"
                      value={item.itemDetails}
                      onChange={(e) =>
                        updateItem(item.id, "itemDetails", e.target.value)
                      }
                      placeholder="Description"
                    />
                  </td>
                  <td>
                    <select
                      value={item.account}
                      onChange={(e) =>
                        updateItem(item.id, "account", e.target.value)
                      }
                    >
                      <option>Sales</option>
                      <option>Invoice Discount</option>
                      <option>General Income</option>
                      <option>Bad Debt</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) =>
                        updateItem(
                          item.id,
                          "quantity",
                          Number(e.target.value) || 1
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.rate}
                      onChange={(e) =>
                        updateItem(item.id, "rate", Number(e.target.value) || 0)
                      }
                    />
                  </td>
                  <td className="amount">₹{item.amount.toFixed(2)}</td>
                  <td>
                    {formData.items.length > 1 && (
                      <button
                        className="delete-btn"
                        onClick={() => removeItem(item.id)}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addItemRow} className="add-row-btn">
            + Add New Row
          </button>
        </div>

        {/* Summary Section */}
        <div className="calculations">
          <div className="notes-group">
            <label>Customer Notes</label>
            <textarea
              value={formData.customerNotes}
              onChange={(e) =>
                setFormData({ ...formData, customerNotes: e.target.value })
              }
            />
            <label style={{ marginTop: "20px" }}>Terms & Conditions</label>
            <textarea
              value={formData.termsConditions}
              onChange={(e) =>
                setFormData({ ...formData, termsConditions: e.target.value })
              }
            />
          </div>

          <div className="calc-summary">
            <div className="calc-left">
              <div className="calc-row">Sub Total</div>
              <div className="calc-row">
                <span>Discount</span>
                <input
                  type="number"
                  value={formData.discountValue}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      discountValue: Number(e.target.value) || 0,
                    })
                  }
                />
                <select
                  value={formData.discountType}
                  onChange={(e) =>
                    setFormData({ ...formData, discountType: e.target.value })
                  }
                >
                  <option>%</option>
                  <option>₹</option>
                </select>
              </div>
              <div className="calc-row">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.tdsTcs}
                    onChange={(e) =>
                      setFormData({ ...formData, tdsTcs: e.target.checked })
                    }
                  />{" "}
                  TDS/TCS
                </label>
                <select
                  disabled={!formData.tdsTcs}
                  value={formData.tdsTcsPercent}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      tdsTcsPercent: Number(e.target.value),
                    })
                  }
                >
                  <option value={0}>None</option>
                  <option value={5}>5%</option>
                  <option value={10}>10%</option>
                </select>
              </div>
              <div className="calc-row">
                <span>Adjustment</span>
                <input
                  type="number"
                  value={formData.adjustment}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      adjustment: Number(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="calc-row total">Total Amount</div>
            </div>
            <div className="calc-right">
              <div>₹{subtotal.toFixed(2)}</div>
              <div>-₹{discountAmount.toFixed(2)}</div>
              <div>-₹{tdsTcsAmount.toFixed(2)}</div>
              <div>₹{formData.adjustment.toFixed(2)}</div>
              <div className="grand-total">₹{total.toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="action-bar">
          <div className="left-actions">
            <button onClick={() => handleSave("Draft")} className="draft-btn">
              Save as Draft
            </button>
            <button onClick={() => handleSave("Sent")} className="send-btn">
              Save & Send
            </button>
            <button
              onClick={() => navigate("/sales/credit-notes")}
              className="cancel-btn"
            >
              Cancel
            </button>
          </div>
          <div className="total-info">
            <span>Total Amount: ₹{total.toFixed(2)}</span>
            <span>
              Total Quantity:{" "}
              {formData.items.reduce((s, i) => s + i.quantity, 0)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCreditNotes;
